import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from 'src/components/Box';
import { Skeleton } from 'src/components/Skeleton';

type IframelyProps = {
  url: string;
  className?: string;
  style?: React.CSSProperties;
  apiKey?: string;
};

declare global {
  interface Window {
    iframely?: { load: (element?: Element | Document) => void };
    __IFRAMELY_SCRIPT_LOADING__?: Promise<void>;
  }
}

const DEFAULT_IFRAMELY_API_KEY = '6645136f4fdd7641bd9b4071a581d937';
const EMBED_JS_SRC = 'https://cdn.iframe.ly/embed.js';

function ensureIframelyScriptLoaded(apiKey?: string): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.__IFRAMELY_SCRIPT_LOADING__)
    return window.__IFRAMELY_SCRIPT_LOADING__;

  window.__IFRAMELY_SCRIPT_LOADING__ = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-iframely]'
    );
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.setAttribute('data-iframely', 'true');
    const key = apiKey || DEFAULT_IFRAMELY_API_KEY;
    const url = new URL(EMBED_JS_SRC);
    if (key) url.searchParams.set('api_key', key);
    url.searchParams.set('parent', 'iframely-embed-container');
    script.src = url.toString();
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load Iframely embed.js'));
    document.head.appendChild(script);
  });

  return window.__IFRAMELY_SCRIPT_LOADING__;
}

export const Iframely: React.FC<IframelyProps> = ({
  url,
  className,
  style,
  apiKey,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [contentHtml, setContentHtml] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const requestUrl = useMemo(() => {
    if (!url) return null;
    const key = apiKey || DEFAULT_IFRAMELY_API_KEY;
    const api = new URL('https://iframe.ly/api/iframely');
    api.searchParams.set('url', url);
    if (key) api.searchParams.set('key', key);
    api.searchParams.set('iframe', '1');
    api.searchParams.set('omit_script', '1');
    return api.toString();
  }, [url, apiKey]);

  useEffect(() => {
    let cancelled = false;
    if (!requestUrl) return;
    setIsLoaded(false);
    setError(null);
    fetch(requestUrl)
      .then((res) => res.json())
      .then((res) => {
        if (cancelled) return;
        if (res?.html) {
          setContentHtml(res.html);
          setIsLoaded(true);
        } else if (res?.error) {
          setError(`${res.error}`);
          setIsLoaded(true);
        } else {
          setError('empty');
          setIsLoaded(true);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError('network');
        setIsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [requestUrl]);

  useEffect(() => {
    if (!contentHtml) return;
    ensureIframelyScriptLoaded(apiKey)
      .then(() => {
        if (!window.iframely) return;
        const root = containerRef.current || document;
        window.iframely.load(root);
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Iframely error');
      });
  }, [contentHtml, apiKey]);

  if (error) return <Box className={className} style={style} />;
  if (!isLoaded)
    return <Skeleton variant="rounded" sx={{ width: '100%', height: 180 }} />;

  return (
    <Box sx={{ maxWidth: 320 }}>
      <Box
        ref={containerRef}
        className={className}
        style={style}
        sx={{ width: '100%' }}
        dangerouslySetInnerHTML={{ __html: contentHtml || '' }}
      />
    </Box>
  );
};

export default Iframely;
